import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Checkbox, TextInput } from "@/components/ui/Field";
import { formatMahnungDate, isoDatumInTagen } from "@/lib/mahnwesen";

const PRESETS = [7, 14, 30];

function naechsterTag(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return "";
  const start = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return isoDatumInTagen(1, start);
}

export function ZahlungsfristDialog({
  open,
  onClose,
  dokumentNummer,
  kundeName,
  aktuelleFaelligkeit,
  pausiert,
  intent,
  busy,
  onSubmit,
  onFortsetzenOhneFrist,
}: {
  open: boolean;
  onClose: () => void;
  dokumentNummer: string | null;
  kundeName: string | null;
  aktuelleFaelligkeit: string | null;
  pausiert: boolean;
  intent: "frist" | "fortsetzen";
  busy: boolean;
  onSubmit: (body: { faelligkeitsdatum: string; pause_aufheben: boolean }) => void;
  onFortsetzenOhneFrist?: () => void;
}) {
  const [modus, setModus] = useState<"tage" | "datum">("tage");
  const [tage, setTage] = useState(14);
  const [datum, setDatum] = useState(() => isoDatumInTagen(14));
  const [pauseAufheben, setPauseAufheben] = useState(true);

  useEffect(() => {
    if (!open) return;
    setModus("tage");
    setTage(14);
    setDatum(isoDatumInTagen(14));
    setPauseAufheben(true);
  }, [open, dokumentNummer]);

  const faellig = modus === "tage" ? isoDatumInTagen(Number.isFinite(tage) ? tage : 0) : datum;
  const verzugAb = naechsterTag(faellig);
  const titel = intent === "fortsetzen" ? "Mahnung fortsetzen" : "Zahlungsfrist neu setzen";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={titel}
      description={
        kundeName
          ? `${dokumentNummer || "Rechnung"} · ${kundeName}`
          : dokumentNummer || "Rechnung"
      }
      size="md"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {intent === "fortsetzen" && onFortsetzenOhneFrist ? (
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={onFortsetzenOhneFrist}
            >
              Ohne neue Frist fortsetzen
            </Button>
          ) : null}
          <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            type="button"
            loading={busy}
            disabled={!faellig}
            onClick={() => onSubmit({ faelligkeitsdatum: faellig, pause_aufheben: pausiert && pauseAufheben })}
          >
            {intent === "fortsetzen" ? "Mit neuer Frist fortsetzen" : "Frist speichern"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 text-sm">
        <p className="text-label-secondary">
          Die Rechnung bekommt ein neues Zahlungsziel
          {aktuelleFaelligkeit ? ` (bisher fällig am ${formatMahnungDate(aktuelleFaelligkeit)})` : ""}.
          Ab dem Folgetag ist sie wieder überfällig, und der Mahnlauf schlägt die nächste Stufe vor.
        </p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((wert) => (
            <button
              key={wert}
              type="button"
              className={
                modus === "tage" && tage === wert
                  ? "rounded-full bg-accent px-3 py-1 text-xs font-semibold text-label-on-accent"
                  : "rounded-full bg-fill-tertiary px-3 py-1 text-xs font-medium text-label"
              }
              onClick={() => {
                setModus("tage");
                setTage(wert);
              }}
            >
              {wert} Tage ab heute
            </button>
          ))}
          <button
            type="button"
            className={
              modus === "datum"
                ? "rounded-full bg-accent px-3 py-1 text-xs font-semibold text-label-on-accent"
                : "rounded-full bg-fill-tertiary px-3 py-1 text-xs font-medium text-label"
            }
            onClick={() => setModus("datum")}
          >
            Datum wählen
          </button>
        </div>
        {modus === "tage" ? (
          <TextInput
            label="Tage ab heute"
            type="number"
            min={0}
            max={365}
            value={tage}
            onChange={(e) => setTage(Math.max(0, Math.min(365, Number(e.target.value) || 0)))}
          />
        ) : (
          <TextInput
            label="Neues Fälligkeitsdatum"
            type="date"
            value={datum}
            onChange={(e) => setDatum(e.target.value)}
          />
        )}
        <p className="rounded-ios bg-fill-tertiary/60 px-3 py-2 text-label">
          Fällig am <strong>{formatMahnungDate(faellig)}</strong>
          {verzugAb ? (
            <>
              {". Überfällig und in Verzug ab "}
              <strong>{formatMahnungDate(verzugAb)}</strong>.
            </>
          ) : (
            "."
          )}
        </p>
        {pausiert ? (
          <Checkbox
            label="Pause aufheben"
            description="Danach läuft der Mahnlauf nach der neuen Frist von allein weiter."
            checked={pauseAufheben}
            onChange={(e) => setPauseAufheben(e.target.checked)}
          />
        ) : null}
      </div>
    </Dialog>
  );
}
